/**
 * One tick of the world.
 *
 * `stepSimulation` is the only function in the codebase that advances the game.
 * Intents are applied before it (see `intents.ts`); everything that happens *to*
 * a kingdom rather than *because* of it happens here.
 *
 * ## Why the steps run in this order
 *
 * Economy → population → troop redeployment → combat → elimination. The order is
 * not arbitrary; each step consumes what the previous one produced, and running
 * them the other way round changes the game:
 *
 * 1. **Economy** first, because population growth is capped by *this* tick's food
 *    production. Growing first would let a kingdom feed a population its land
 *    can't support for one tick, which compounds into a free ratchet over a
 *    35-minute session.
 * 2. **Population** second, because troop strength is a slice of it. A starving
 *    kingdom must go into combat this tick already weakened — that is the whole
 *    reason starvation exists as a mechanic.
 * 3. **Troop redeployment** third, so combat resolves against where the troops
 *    actually are now, including the fraction of a surge that has arrived. Doing
 *    it after combat would give every surge a one-tick lag and make the capture
 *    meter lead the visible troop counts.
 * 4. **Combat** fourth. It is the only step that changes ownership, so running it
 *    last among the mutating steps means territory counts are stable for the
 *    whole of the economy — no kingdom is ever taxed for land it lost mid-tick.
 * 5. **Elimination** last, because it is a pure observation of the outcome of
 *    step 4, followed by a re-clamp of every store against the territory the
 *    kingdom finished the tick with. Step 1 cannot do that job alone: a kingdom
 *    that *loses* land in step 4 would otherwise sit above its storage cap until
 *    the next tick, and that tick is broadcast.
 *
 * ## The one invariant that matters most
 *
 * Combat is computed in full **before** any of it is applied. Troop counts and
 * ownership are read from a world that no combat has touched yet, so which hex
 * the map iterator happens to visit first cannot decide who wins. See
 * `runCombat` — the two-phase structure *is* the snapshot, and `simulation.test.ts`
 * pins it down both by comparing mirrored maps built in opposite insertion orders
 * and by checking that a hex flipping owner does not retroactively change the
 * fight next door.
 *
 * Every rate here is per second and multiplied by `dtSeconds`, so changing the
 * tick rate does not silently rebalance the game.
 */
import { CAPTURE_DECAY_RATE, WORLD_EVENTS, eventYieldScale, NEUTRAL_LAND_CAPTURABLE_BY_PRESSURE, CAPTURE_TROOP_CARRYOVER, FOOD_UPKEEP_PER_POP, POPULATION_GROWTH_RATE, RESOURCE_TYPES, STARVATION_RATE, TROOP_REDEPLOY_RATE, buildingStorageBonus, emptyBundle, hexKey, hexYield, parseBorderSegmentId, populationCap, resolveCombat, resourceCap, troopCount, troopTargetWeight, workerCount, workerEfficiency, } from "kngdm-shared";
import { isValidSurgeTarget } from "./borders.js";
import { hexNeighbourRecords, isFortified, isFrontierHex, markDirty } from "./state.js";
import { stepWorldEvents } from "./worldEvents.js";
/**
 * Advance the world by `dtSeconds`, mutating `state` in place.
 *
 * `state.tick` is *not* incremented here — the session owns the tick counter and
 * sets it from the `GameLoop`'s `TickContext` before calling. This function only
 * reads it, to stamp `eliminatedAtTick`.
 */
export function stepSimulation(state, dtSeconds) {
    // A zero or nonsensical dt is a no-op rather than a crash: a stalled or
    // rewound clock must never be able to produce NaN in the authoritative state.
    if (!Number.isFinite(dtSeconds) || dtSeconds <= 0)
        return [];
    // Events resolve before the economy so a disaster landing this tick is felt
    // this tick, rather than a tick later than the client is told about it.
    const landed = stepWorldEvents(state);
    runEconomyAndPopulation(state, dtSeconds);
    runTroopRedeployment(state, dtSeconds);
    runCombat(state, dtSeconds);
    runElimination(state);
    runStorageSettlement(state);
    return landed;
}
// ---------------------------------------------------------------------------
// 1 & 2 — Economy and population
// ---------------------------------------------------------------------------
/**
 * Accrue resources and move population toward what the land can feed.
 *
 * These two are one pass because population depends only on the kingdom's own
 * food production this tick — there is no cross-kingdom coupling to order, and
 * splitting them would mean computing every kingdom's yield twice.
 */
function runEconomyAndPopulation(state, dt) {
    // Once per tick, not once per kingdom: the fold is over active events, which
    // are global, so recomputing it for each of six kingdoms was six times the work
    // for an identical answer.
    const scaleByKey = eventScalesByHex(state);
    for (const kingdom of state.kingdoms.values()) {
        if (kingdom.eliminated)
            continue;
        const territoryCount = kingdom.hexes.size;
        const { yieldPerSecond, storageBonus } = kingdomYield(state, kingdom, territoryCount, scaleByKey);
        // Food upkeep scales with population, not with land: a big army on a small
        // kingdom starves, which is what stops troops being free.
        const upkeep = kingdom.population * FOOD_UPKEEP_PER_POP;
        const resources = kingdom.resources;
        for (const type of RESOURCE_TYPES) {
            const net = type === "food" ? yieldPerSecond.food - upkeep : yieldPerSecond[type];
            const next = resources[type] + net * dt;
            // Per-resource, because Aether's ceiling is flat while the rest scale with
            // territory. Clamped on both ends every tick, so a kingdom that *shrinks*
            // also loses the banked surplus its lost land was holding.
            const cap = resourceCap(type, territoryCount, storageBonus);
            resources[type] = next < 0 ? 0 : next > cap ? cap : next;
        }
        // Starving means the store hit zero with demand still outstanding, not
        // merely that the net rate is negative — a kingdom eating into a full
        // granary is not starving.
        const starving = resources.food + (yieldPerSecond.food - upkeep) * dt < 0;
        // The cap is driven by *gross* production: what the fields grow, not what is
        // left after the current population has eaten. Using net would make the cap
        // fall as population rises and produce an oscillation instead of a ceiling.
        const target = populationCap(yieldPerSecond.food);
        if (starving) {
            kingdom.population -= kingdom.population * STARVATION_RATE * dt;
        }
        else {
            // Signed on purpose: a kingdom over its cap drifts back down at the same
            // rate it grew, so losing farmland costs population rather than freezing it.
            kingdom.population += (target - kingdom.population) * POPULATION_GROWTH_RATE * dt;
        }
        if (!(kingdom.population > 0))
            kingdom.population = 0;
    }
}
/**
 * Yield multipliers per affected hex, folded across overlapping events.
 *
 * Returns only hexes actually under something, so the common case — a hex under
 * no event at all — is a map miss rather than a scan of the active list. Built
 * once per kingdom yield fold rather than per hex, because otherwise the event
 * system lands in the hottest loop in the simulation.
 */
function eventScalesByHex(state) {
    // The overwhelmingly common case, and worth its own exit: most ticks of most
    // sessions have nothing happening, and allocating two maps per kingdom per tick
    // to discover that cost ~25% of total simulation time when it was measured.
    if (state.activeEvents.length === 0)
        return null;
    const byKey = new Map();
    for (const active of state.activeEvents) {
        const scale = WORLD_EVENTS[active.event].yieldScale;
        if (Object.keys(scale).length === 0)
            continue;
        for (const key of active.hexKeys) {
            const list = byKey.get(key);
            if (list === undefined)
                byKey.set(key, [scale]);
            else
                list.push(scale);
        }
    }
    const result = new Map();
    for (const [key, scales] of byKey)
        result.set(key, eventYieldScale(scales));
    return result;
}
/**
 * Per-second yield of all owned hexes after worker coverage, plus the storage
 * bonus its buildings add.
 *
 * Both come out of one walk over the territory. They are unrelated quantities,
 * but the walk is the expensive part — up to 1600 hexes per kingdom per tick — and
 * doing it twice to keep the function names tidy would double the hottest loop in
 * the simulation.
 */
function kingdomYield(state, kingdom, territoryCount, scaleByKey) {
    const total = emptyBundle();
    let storageBonus = 0;
    for (const key of kingdom.hexes) {
        const record = state.hexes.get(key);
        if (record === undefined)
            continue;
        const perHex = hexYield(record.biome, record.building, record.special);
        const scale = scaleByKey?.get(key);
        for (const type of RESOURCE_TYPES) {
            total[type] += scale === undefined ? perHex[type] : perHex[type] * scale[type];
        }
        storageBonus += buildingStorageBonus(record.building);
    }
    // The coupling that makes expansion a decision: land you have no workers for
    // dilutes the output of land you do.
    const efficiency = workerEfficiency(workerCount(kingdom.population, kingdom.workerRatio), territoryCount);
    for (const type of RESOURCE_TYPES)
        total[type] *= efficiency;
    return { yieldPerSecond: total, storageBonus };
}
/**
 * Spread each kingdom's troop pool across its frontier, gradually.
 *
 * Troops are not a stockpile that moves; they are a *distribution* of the
 * kingdom's fighting population over the hexes that touch someone else. Each hex
 * closes a fraction of the gap to its share per second, so a surge has to travel.
 * Instant redeployment would turn the game's one core combat verb into a button.
 */
function runTroopRedeployment(state, dt) {
    for (const kingdom of state.kingdoms.values()) {
        if (kingdom.eliminated)
            continue;
        const surgeCounts = pruneAndCountSurges(state, kingdom);
        const pool = troopCount(kingdom.population, kingdom.workerRatio);
        const frontier = [];
        let weightTotal = 0;
        for (const key of kingdom.hexes) {
            const record = state.hexes.get(key);
            if (record === undefined)
                continue;
            if (isFrontierHex(state, record)) {
                const weight = troopTargetWeight(surgeCounts.get(key) ?? 0);
                frontier.push({ record, weight });
                weightTotal += weight;
            }
            else {
                // Interior hexes are not defended by standing troops; holding them there
                // would let a kingdom soak pressure with soldiers nobody can reach.
                moveTroopsToward(state, record, 0, dt);
            }
        }
        // No frontier means nowhere to stand: the pool is simply held in reserve and
        // reappears the moment this kingdom touches someone again.
        if (weightTotal <= 0)
            continue;
        for (const entry of frontier) {
            moveTroopsToward(state, entry.record, (pool * entry.weight) / weightTotal, dt);
        }
    }
}
/**
 * Count live surges per owned hex, dropping the ones that no longer address a
 * real border.
 *
 * Pruning here rather than in the intent handler is what makes a completed push
 * release its own surge: once the target hex is ours, the segment stops being a
 * border and the order retires itself. It also keeps `surges` from growing
 * without bound across a long session.
 */
function pruneAndCountSurges(state, kingdom) {
    const counts = new Map();
    // Snapshot the ids: deleting from a Set while iterating it is legal but reads
    // as a bug to the next person, and the set is small.
    for (const id of [...kingdom.surges]) {
        if (!isValidSurgeTarget(state, kingdom.id, id)) {
            kingdom.surges.delete(id);
            continue;
        }
        const key = hexKey(parseBorderSegmentId(id).hex);
        counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
}
function moveTroopsToward(state, record, target, dt) {
    const next = record.troops + (target - record.troops) * TROOP_REDEPLOY_RATE * dt;
    setTroops(state, record, next);
}
/**
 * Resolve every contested hex.
 *
 * Phase 1 reads the world and computes outcomes; phase 2 writes them. Nothing in
 * phase 1 mutates, so every fight sees identical pre-tick troop counts and
 * ownership no matter what order the map is iterated in. That separation is the
 * whole reason this is two loops instead of one — an in-place resolution makes
 * the winner of a three-way border depend on `Map` insertion order, which is a
 * bug you cannot see through a UI and cannot reproduce reliably.
 */
function runCombat(state, dt) {
    const outcomes = [];
    for (const record of state.hexes.values()) {
        const outcome = computeCombat(state, record);
        if (outcome !== null)
            outcomes.push(outcome);
    }
    // Losses are accumulated rather than applied per fight: a hex flanked by three
    // contested neighbours bleeds into all three, and summing before clamping
    // keeps the total independent of the order they were resolved in.
    const troopLoss = new Map();
    const populationLoss = new Map();
    const flips = [];
    for (const outcome of outcomes) {
        const record = outcome.record;
        if (outcome.captureDelta > 0 && outcome.contender !== null) {
            // Progress belongs to one attacker. A new contender starts from zero
            // rather than inheriting the last one's work.
            if (record.captureBy !== outcome.contender) {
                record.captureBy = outcome.contender;
                setCapture(state, record, 0);
            }
            setCapture(state, record, record.capture + outcome.captureDelta * dt);
            if (record.capture >= 1)
                flips.push(outcome);
        }
        else {
            const decayed = record.capture + outcome.captureDelta * dt;
            if (decayed <= 0) {
                setCapture(state, record, 0);
                record.captureBy = null;
            }
            else {
                setCapture(state, record, decayed);
            }
        }
        if (outcome.attritionPerSide > 0) {
            const loss = outcome.attritionPerSide * dt;
            bleed(loss, outcome.defenceSources, outcome.defenderId, troopLoss, populationLoss);
            bleed(loss, outcome.attackSources, outcome.contender, troopLoss, populationLoss);
        }
    }
    applyTroopLosses(state, troopLoss);
    applyPopulationLosses(state, populationLoss);
    // Flips last, so a captured hex's garrison is set from the attacker's
    // pressure and not immediately clawed back by that fight's own attrition.
    for (const outcome of flips)
        applyFlip(state, outcome);
}
/**
 * Build and resolve one hex's fight, or return `null` if there is nothing to do.
 *
 * A hex is contested when it is **owned** and adjacent to land owned by someone
 * else. Unclaimed land is not a combat target: it is bought with resources via
 * `claimHex`, never taken with troops. See NEUTRAL_LAND_CAPTURABLE_BY_PRESSURE
 * in balance.ts for why that separation is the difference between a game with
 * an economy and a game without one.
 */
function computeCombat(state, record) {
    if (record.owner === null && !NEUTRAL_LAND_CAPTURABLE_BY_PRESSURE) {
        // Neutral land never accumulates capture progress, but a hex that was
        // contested and then lost its owner could still carry a stale meter.
        if (record.capture <= 0)
            return null;
        return {
            record,
            contender: null,
            captureDelta: -CAPTURE_DECAY_RATE,
            attackPressure: 0,
            attritionPerSide: 0,
            defenderId: null,
            defenceSources: [],
            attackSources: [],
        };
    }
    const neighbours = hexNeighbourRecords(state, record.coord);
    let defenderAdjacentTroops = 0;
    const defenceSources = [];
    const attackerTroops = new Map();
    const attackerSources = new Map();
    if (record.owner !== null)
        defenceSources.push({ record, troops: record.troops });
    for (const neighbour of neighbours) {
        if (neighbour.owner === null)
            continue;
        if (neighbour.owner === record.owner) {
            defenderAdjacentTroops += neighbour.troops;
            defenceSources.push({ record: neighbour, troops: neighbour.troops });
            continue;
        }
        const pressure = attackerTroops.get(neighbour.owner) ?? 0;
        attackerTroops.set(neighbour.owner, pressure + neighbour.troops);
        const source = { record: neighbour, troops: neighbour.troops };
        const sources = attackerSources.get(neighbour.owner);
        if (sources === undefined)
            attackerSources.set(neighbour.owner, [source]);
        else
            sources.push(source);
    }
    if (attackerTroops.size === 0) {
        // Uncontested. Only worth an outcome if there is banked progress to burn off:
        // an attacker who walks away must not keep their meter warm.
        if (record.capture <= 0)
            return null;
        return {
            record,
            contender: null,
            captureDelta: -CAPTURE_DECAY_RATE,
            attackPressure: 0,
            attritionPerSide: 0,
            defenderId: record.owner,
            defenceSources: [],
            attackSources: [],
        };
    }
    // Sorted by id so that two attackers with *identical* pressure resolve the
    // same way every time. `resolveCombat` takes the first strict maximum, which
    // would otherwise inherit the iteration order of the neighbour scan.
    const attackers = [...attackerTroops]
        .map(([kingdomId, troops]) => ({ kingdomId, troops }))
        .sort((a, b) => a.kingdomId.localeCompare(b.kingdomId));
    const context = {
        biome: record.biome,
        building: record.building,
        fortified: isFortified(record, state.tick),
        defenderId: record.owner,
        defenderTroopsOnHex: record.owner === null ? 0 : record.troops,
        defenderAdjacentTroops,
        attackers,
    };
    const result = resolveCombat(context);
    return {
        record,
        contender: result.contender,
        captureDelta: result.captureDelta,
        attackPressure: result.attackPressure,
        attritionPerSide: result.attritionPerSide,
        defenderId: record.owner,
        defenceSources,
        attackSources: result.contender === null ? [] : attackerSources.get(result.contender) ?? [],
    };
}
/**
 * Charge one side's attrition, split across the hexes that supplied the pressure
 * and against the kingdom's population.
 *
 * Deducting from population as well as from the hex is deliberate, and is the
 * step that makes war cost something. Hex troops are a *view* of the population
 * pool — redeployment refills them from it within a couple of seconds — so
 * bleeding the hex alone would make attrition invisible and combat free, which
 * is exactly the failure mode `shared/src/rules.ts` warns about ("war costs
 * population — which costs workers — which costs resources").
 */
function bleed(amount, sources, kingdomId, troopLoss, populationLoss) {
    if (kingdomId === null || sources.length === 0)
        return;
    let available = 0;
    for (const source of sources)
        available += source.troops;
    if (available <= 0)
        return;
    const loss = Math.min(amount, available);
    for (const source of sources) {
        const share = loss * (source.troops / available);
        troopLoss.set(source.record.key, (troopLoss.get(source.record.key) ?? 0) + share);
    }
    populationLoss.set(kingdomId, (populationLoss.get(kingdomId) ?? 0) + loss);
}
function applyTroopLosses(state, troopLoss) {
    for (const [key, loss] of troopLoss) {
        const record = state.hexes.get(key);
        if (record === undefined)
            continue;
        setTroops(state, record, record.troops - loss);
    }
}
function applyPopulationLosses(state, populationLoss) {
    for (const [kingdomId, loss] of populationLoss) {
        const kingdom = state.kingdoms.get(kingdomId);
        if (kingdom === undefined)
            continue;
        kingdom.population -= loss;
        if (!(kingdom.population > 0))
            kingdom.population = 0;
    }
}
/** Hand a hex to its captor: both territory sets, the meter, and the garrison. */
function applyFlip(state, outcome) {
    const record = outcome.record;
    const contender = outcome.contender;
    if (contender === null)
        return;
    const winner = state.kingdoms.get(contender);
    if (winner === undefined) {
        // Unreachable in a consistent state, but a missing kingdom must not strand a
        // hex with a full capture meter that can never discharge.
        setCapture(state, record, 0);
        record.captureBy = null;
        return;
    }
    if (record.owner !== null)
        state.kingdoms.get(record.owner)?.hexes.delete(record.key);
    record.owner = contender;
    winner.hexes.add(record.key);
    // The meter measures progress against the *previous* owner, so it is stale by
    // definition the instant ownership changes.
    setCapture(state, record, 0);
    record.captureBy = null;
    // The attackers who took the hex are standing on it. The rest of the pool is
    // renormalised by the next tick's redeployment, so this is a seed rather than
    // an exact transfer — it stops a freshly taken hex from being free to retake.
    setTroops(state, record, outcome.attackPressure * CAPTURE_TROOP_CARRYOVER);
    // The building survives the conquest: taking a sawmill is worth more than
    // razing it, and it gives a losing kingdom something concrete to lose.
    markDirty(state, record);
}
// ---------------------------------------------------------------------------
// 5 — Elimination
// ---------------------------------------------------------------------------
function runElimination(state) {
    for (const kingdom of state.kingdoms.values()) {
        if (kingdom.eliminated || kingdom.hexes.size > 0)
            continue;
        kingdom.eliminated = true;
        kingdom.eliminatedAtTick = state.tick;
        // Orders against a border it no longer has.
        kingdom.surges.clear();
    }
}
// ---------------------------------------------------------------------------
// 6 — Storage settlement
// ---------------------------------------------------------------------------
/**
 * Re-clamp every store to the cap the kingdom's *final* territory supports.
 *
 * Step 1 clamps against the territory a kingdom held at the start of the tick,
 * which is the right number for accrual but stale by the time combat has taken
 * land off it. Without this pass a kingdom that loses a hex ends the tick — and
 * is broadcast — holding more than its own storage cap.
 */
function runStorageSettlement(state) {
    for (const kingdom of state.kingdoms.values()) {
        const resources = kingdom.resources;
        // Recomputed rather than carried over from the economy step, because combat
        // runs in between: a kingdom that just lost the hex its storehouse stood on
        // must be clamped against the cap it *ends* the tick with.
        let storageBonus = 0;
        for (const key of kingdom.hexes) {
            storageBonus += buildingStorageBonus(state.hexes.get(key)?.building ?? null);
        }
        for (const type of RESOURCE_TYPES) {
            const cap = resourceCap(type, kingdom.hexes.size, storageBonus);
            const value = resources[type];
            if (value > cap)
                resources[type] = cap;
            else if (!(value > 0))
                resources[type] = 0;
        }
    }
}
// ---------------------------------------------------------------------------
// Dirty tracking
//
// Troops and capture change on almost every hex on almost every tick, by amounts
// far below what the wire format carries (`toHexState` rounds troops to 2dp and
// capture to 3dp). Marking a hex dirty only when its *serialised* value would
// differ keeps the Phase 10 delta stream proportional to what a player can
// actually see, instead of to the number of hexes on the map.
// ---------------------------------------------------------------------------
function setTroops(state, record, value) {
    const next = value > 0 ? value : 0;
    const changed = Math.round(next * 100) !== Math.round(record.troops * 100);
    record.troops = next;
    if (changed)
        markDirty(state, record);
}
function setCapture(state, record, value) {
    const next = value < 0 ? 0 : value > 1 ? 1 : value;
    const changed = Math.round(next * 1000) !== Math.round(record.capture * 1000);
    record.capture = next;
    if (changed)
        markDirty(state, record);
}
//# sourceMappingURL=simulation.js.map